export interface SystemPreset {
  id: string;
  name: string;
  capacityKva: number;
  inverterCapacityKw: number;
  batteryCapacityKwh: number;
  solarPanelCapacityKw: number;
  recommendedFor: string;
  typicalAppliances: string[];
  priceNgn: number;
  originalPriceNgn?: number;
  monthlySavingsNgn: number;
  paybackMonths: number;
  badge?: string;
  features: string[];
}

export interface NigerianBenchmarks {
  petrolPricePerLiter: number;
  dieselPricePerLiter: number;
  bandATariffPerKwh: number;
  bandCTariffPerKwh: number;
  generatorFuelRateLitersPerKwh: number;
  averageGridHoursPerDay: number;
  thermalDeratingFactor: number;
  peakSunHoursLagos: number;
  peakSunHoursAbuja: number;
  peakSunHoursPH: number;
}

export interface ApplianceItem {
  id: string;
  name: string;
  defaultWatts: number;
  defaultHours: number;
  category: 'cooling' | 'entertainment' | 'kitchen' | 'utility' | 'lighting';
  icon: string;
}

export const NIGERIAN_BENCHMARKS: NigerianBenchmarks = {
  petrolPricePerLiter: 1150, // NGN per liter (current average)
  dieselPricePerLiter: 1450, // NGN per liter
  bandATariffPerKwh: 225,    // NGN per kWh (Band A 20+ hrs)
  bandCTariffPerKwh: 90,     // NGN per kWh (Lower bands with erratic supply)
  generatorFuelRateLitersPerKwh: 0.45, // Liters per kWh generated on average generator
  averageGridHoursPerDay: 5, // Typical Nigerian grid availability
  thermalDeratingFactor: 0.18, // 18% African thermal/heat panel loss
  peakSunHoursLagos: 4.8,
  peakSunHoursAbuja: 5.5,
  peakSunHoursPH: 4.6,
};

export const COMMON_APPLIANCES: ApplianceItem[] = [
  { id: 'ac_1hp', name: '1.5 HP Inverter AC', defaultWatts: 1200, defaultHours: 6, category: 'cooling', icon: 'Wind' },
  { id: 'fridge', name: 'Double Door Refrigerator / Freezer', defaultWatts: 200, defaultHours: 24, category: 'kitchen', icon: 'Refrigerator' },
  { id: 'tv_55', name: '55" Smart LED TV + Decoder', defaultWatts: 150, defaultHours: 8, category: 'entertainment', icon: 'Tv' },
  { id: 'lighting', name: 'LED Bulbs & Ceiling Fans (Whole House)', defaultWatts: 180, defaultHours: 10, category: 'lighting', icon: 'Lightbulb' },
  { id: 'pumping_machine', name: 'Water Pumping Machine (1HP)', defaultWatts: 750, defaultHours: 1, category: 'utility', icon: 'Droplets' },
  { id: 'washing_machine', name: 'Automatic Washing Machine', defaultWatts: 500, defaultHours: 1, category: 'utility', icon: 'Shirt' },
  { id: 'microwave', name: 'Microwave Oven', defaultWatts: 1200, defaultHours: 0.5, category: 'kitchen', icon: 'Microwave' },
  { id: 'workstation', name: 'Laptops, Monitors & Starlink Router', defaultWatts: 150, defaultHours: 10, category: 'utility', icon: 'Laptop' },
  { id: 'iron', name: 'Pressing Iron', defaultWatts: 1500, defaultHours: 0.5, category: 'utility', icon: 'Zap' },
];

export const NIGERIAN_SOLAR_PRESETS: SystemPreset[] = [
  {
    id: 'starter_1_5kva',
    name: '1.5kVA Essential Starter',
    capacityKva: 1.5,
    inverterCapacityKw: 1.2,
    batteryCapacityKwh: 2.4, // e.g. 1 x 24V 100Ah LiFePO4
    solarPanelCapacityKw: 1.2, // 4 x 300W panels
    recommendedFor: 'Apartments, Remote Workstations, Basic Lighting & Entertainment',
    typicalAppliances: ['TV & Decoder', 'LED Bulbs & Fans', 'Laptops & Routers', 'Small Fridge (Inverter)'],
    priceNgn: 980000,
    originalPriceNgn: 1150000,
    monthlySavingsNgn: 75000,
    paybackMonths: 13,
    badge: 'Best for Apartments',
    features: [
      'Pure Sine Wave Inverter',
      'Grade-A Lithium Iron Phosphate (LiFePO4) Battery (6,000+ Cycles)',
      'Monocrystalline PERC Solar Panels with 25-Year Performance Warranty',
      'Smart MPPT Solar Charge Controller with Mobile Monitoring App',
      'Professional Installation & 5-Year Comprehensive Warranty'
    ]
  },
  {
    id: 'standard_3_5kva',
    name: '3.5kVA Smart Home Standard',
    capacityKva: 3.5,
    inverterCapacityKw: 3.0,
    batteryCapacityKwh: 4.8, // 48V 100Ah LiFePO4
    solarPanelCapacityKw: 2.8, // 6 x 450W panels
    recommendedFor: '3-Bedroom Duplex, Families with 1 AC, Fridge, and Heavy Appliances',
    typicalAppliances: ['1 x 1.5HP Inverter AC', 'Double Door Fridge', 'TVs, Soundbar & Decoders', 'Lighting & Fans', 'Water Pump'],
    priceNgn: 2250000,
    originalPriceNgn: 2500000,
    monthlySavingsNgn: 165000,
    paybackMonths: 14,
    badge: 'Most Popular',
    features: [
      '3.5kVA Hybrid Smart Inverter (Grid + Generator + Solar Auto-Switch)',
      '4.8kWh LiFePO4 Wall-Mount Battery Bank with BMS',
      '2.8kW High-Efficiency Monocrystalline Panels',
      'Zero Noise, Zero Fuel, Automatic Seamless Transfer (<10ms)',
      'Remote Wi-Fi Monitoring + 5-Year Maintenance Support'
    ]
  },
  {
    id: 'executive_7_5kva',
    name: '7.5kVA Executive Powerhouse',
    capacityKva: 7.5,
    inverterCapacityKw: 6.2,
    batteryCapacityKwh: 10.0, // 48V 200Ah LiFePO4
    solarPanelCapacityKw: 5.5, // 12 x 450W panels
    recommendedFor: 'Large Duplexes, Luxury Homes, Small Offices & Clinics',
    typicalAppliances: ['2 x 1.5HP Inverter ACs', 'Large Double Fridge & Freezer', 'Full House Lighting & Fans', 'Washing Machine & Microwave', 'Office Computers & Printers'],
    priceNgn: 4650000,
    originalPriceNgn: 5100000,
    monthlySavingsNgn: 340000,
    paybackMonths: 14,
    badge: 'Commercial Grade',
    features: [
      'Parallel-Capable 7.5kVA Pure Sine Wave Hybrid Inverter',
      '10kWh Expandable Lithium Battery Storage',
      '5.5kW Tier-1 Monocrystalline Solar Array',
      'Advanced Cloud Monitoring with AI Load Optimization',
      'Lightning & Surge Protection System Included'
    ]
  },
  {
    id: 'enterprise_10kva',
    name: '10kVA+ Enterprise Solar Hub',
    capacityKva: 10.0,
    inverterCapacityKw: 10.0,
    batteryCapacityKwh: 15.0,
    solarPanelCapacityKw: 8.2,
    recommendedFor: 'Mansions, Banks, Supermarkets, Schools & Corporate Offices',
    typicalAppliances: ['Multiple ACs (Up to 4)', 'Heavy Commercial Refrigeration', 'Elevators/Pumps', 'Full IT Infrastructure', 'Lighting & Security Systems'],
    priceNgn: 7800000,
    originalPriceNgn: 8500000,
    monthlySavingsNgn: 620000,
    paybackMonths: 13,
    badge: 'Ultimate Power',
    features: [
      'Industrial-Grade Three-Phase / Single-Phase 10kVA Inverter',
      '15kWh Modular High-Voltage Lithium Battery Pack',
      '8.2kW Heavy-Duty Solar Rooftop Structure',
      'Priority 24/7 Dedicated Engineering Support & SLA',
      'Comprehensive Insurance Cover for 1 Year'
    ]
  }
];

export const NIGERIAN_STATES = [
  { name: 'Lagos State', peakSunHours: 4.8, gridUncertainty: 0.85 },
  { name: 'Abuja (FCT)', peakSunHours: 5.5, gridUncertainty: 0.70 },
  { name: 'Port Harcourt (Rivers)', peakSunHours: 4.6, gridUncertainty: 0.80 },
  { name: 'Ibadan (Oyo)', peakSunHours: 5.1, gridUncertainty: 0.75 },
  { name: 'Kano State', peakSunHours: 6.0, gridUncertainty: 0.85 },
  { name: 'Enugu State', peakSunHours: 5.0, gridUncertainty: 0.78 },
  { name: 'Kaduna State', peakSunHours: 5.8, gridUncertainty: 0.82 },
  { name: 'Delta State', peakSunHours: 4.7, gridUncertainty: 0.80 },
];
